import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    TextField,
    Button,
    Avatar,
    Card,
    CardContent,
    Tabs,
    Tab,
    CircularProgress,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Switch,
    FormControlLabel,
    Badge
} from '@mui/material';
import {
    Person as PersonIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Psychology as AIIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    EmojiEvents as TrophyIcon,
    Assignment as TaskIcon,
    AttachMoney as MoneyIcon,
    Settings as SettingsIcon,
    Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';

interface UserProfile {
    id: number;
    user_id: number;
    first_name: string;
    last_name: string;
    display_name: string;
    job_title: string;
    department: string | null;
    phone: string;
    bio: string;
    location: string;
    timezone: string;
    created_at: string;
    updated_at: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`profile-tabpanel-${index}`}
            aria-labelledby={`profile-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export default function UserProfilePage() {
    const [tabValue, setTabValue] = useState(0);
    const [editMode, setEditMode] = useState(false);
    const [profileData, setProfileData] = useState<Partial<UserProfile>>({});
    const queryClient = useQueryClient();

    // Fetch user profile with fallback (no authentication required for demo)
    const { data: profile, isLoading } = useQuery<UserProfile>({
        queryKey: ['userProfile'],
        queryFn: async () => {
            // Always return fallback data for demo purposes
            return {
                id: 1,
                user_id: 2,
                first_name: 'Demo',
                last_name: 'User',
                display_name: 'Demo User',
                job_title: 'Sales Representative',
                department: 'Sales',
                phone: '+1 (555) 123-4567',
                profile_image_url: null,
                bio: 'Experienced sales professional focused on AI-powered CRM solutions.',
                location: 'San Francisco, CA',
                timezone: 'America/Los_Angeles',
                language_preference: 'en',
                linkedin_url: 'https://linkedin.com/in/demo-user',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                // Performance metrics
                quota_attainment: 125,
                deals_closed: 23,
                revenue_generated: 450000,
                activities_completed: 156,
                // Preferences
                email_notifications: true,
                push_notifications: true,
                weekly_digest: true,
                // AI features
                ai_insights_enabled: true,
                auto_follow_up: true,
                smart_scheduling: true
            } as UserProfile;
        }
    });

    // Mock performance metrics
    const performanceMetrics = {
        deals_closed: 23,
        revenue_generated: 1250000,
        activities_completed: 156,
        ai_insights_generated: 89,
        quota_attainment: 125
    };

    // Update profile mutation
    const updateProfileMutation = useMutation({
        mutationFn: async (updatedProfile: Partial<UserProfile>) => {
            return baseClient.put('/user-profiles/profiles/me', updatedProfile);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            setEditMode(false);
        }
    });

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleSave = () => {
        updateProfileMutation.mutate(profileData);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
        }).format(value);
    };

    const getInitials = () => {
        if (!profile) return 'DU';
        return `${profile.first_name?.charAt(0) || 'D'}${profile.last_name?.charAt(0) || 'U'}`;
    };

    if (isLoading) {
        return (
            <Container>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        <PersonIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                        User Profile
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Manage your profile and performance metrics
                    </Typography>
                </Box>
                <Button
                    variant={editMode ? 'outlined' : 'contained'}
                    startIcon={editMode ? <SaveIcon /> : <EditIcon />}
                    onClick={editMode ? handleSave : () => setEditMode(true)}
                    disabled={updateProfileMutation.isLoading}
                >
                    {editMode ? 'Save Changes' : 'Edit Profile'}
                </Button>
            </Box>

            {/* Profile Header Card */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                            <Avatar sx={{ width: 24, height: 24, bgcolor: 'success.main' }}>
                                <AIIcon sx={{ fontSize: 16 }} />
                            </Avatar>
                        }
                    >
                        <Avatar
                            sx={{
                                width: 100,
                                height: 100,
                                bgcolor: 'primary.main',
                                fontSize: '2rem'
                            }}
                        >
                            {getInitials()}
                        </Avatar>
                    </Badge>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" gutterBottom>
                            {profile?.display_name || `${profile?.first_name} ${profile?.last_name}`}
                        </Typography>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {profile?.job_title} • {profile?.department}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip icon={<EmailIcon />} label={`ID: ${profile?.user_id}`} size="small" />
                            <Chip icon={<LocationIcon />} label={profile?.location} size="small" />
                            <Chip icon={<AIIcon />} label="AI-Powered" size="small" color="primary" />
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h4" color="primary">
                            {performanceMetrics.quota_attainment}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Quota Attainment
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Performance Metrics */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrophyIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Deals</Typography>
                            </Box>
                            <Typography variant="h4" color="primary">
                                {performanceMetrics.deals_closed}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Deals closed
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <MoneyIcon color="success" sx={{ mr: 1 }} />
                                <Typography variant="h6">Revenue</Typography>
                            </Box>
                            <Typography variant="h4" color="success.main">
                                {formatCurrency(performanceMetrics.revenue_generated)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total revenue
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TaskIcon color="info" sx={{ mr: 1 }} />
                                <Typography variant="h6">Activities</Typography>
                            </Box>
                            <Typography variant="h4" color="info.main">
                                {performanceMetrics.activities_completed}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Activities completed
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AIIcon color="warning" sx={{ mr: 1 }} />
                                <Typography variant="h6">AI Insights</Typography>
                            </Box>
                            <Typography variant="h4" color="warning.main">
                                {performanceMetrics.ai_insights_generated}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                AI insights generated
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Tabs */}
            <Paper>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Personal Info" icon={<PersonIcon />} />
                        <Tab label="Preferences" icon={<SettingsIcon />} />
                        <Tab label="Notifications" icon={<NotificationsIcon />} />
                    </Tabs>
                </Box>

                {/* Personal Info Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={editMode ? (profileData.first_name || profile?.first_name || '') : (profile?.first_name || '')}
                                onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                                disabled={!editMode}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={editMode ? (profileData.last_name || profile?.last_name || '') : (profile?.last_name || '')}
                                onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                                disabled={!editMode}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Job Title"
                                value={editMode ? (profileData.job_title || profile?.job_title || '') : (profile?.job_title || '')}
                                onChange={(e) => setProfileData({ ...profileData, job_title: e.target.value })}
                                disabled={!editMode}
                                sx={{ mb: 2 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                value={editMode ? (profileData.phone || profile?.phone || '') : (profile?.phone || '')}
                                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                disabled={!editMode}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Location"
                                value={editMode ? (profileData.location || profile?.location || '') : (profile?.location || '')}
                                onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                                disabled={!editMode}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label="Bio"
                                multiline
                                rows={3}
                                value={editMode ? (profileData.bio || profile?.bio || '') : (profile?.bio || '')}
                                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                                disabled={!editMode}
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Preferences Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                AI & System Preferences
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemText primary="Enable AI Recommendations" secondary="Get AI-powered insights and suggestions" />
                                    <FormControlLabel control={<Switch defaultChecked />} label="" />
                                </ListItem>
                                <ListItem>
                                    <ListItemText primary="Auto-generate Insights" secondary="Automatically create insights from activities" />
                                    <FormControlLabel control={<Switch defaultChecked />} label="" />
                                </ListItem>
                                <ListItem>
                                    <ListItemText primary="Real-time Notifications" secondary="Get instant notifications for important events" />
                                    <FormControlLabel control={<Switch defaultChecked />} label="" />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </TabPanel>

                {/* Notifications Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Notification Settings
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <EmailIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Email Notifications" secondary="Receive notifications via email" />
                                    <Switch defaultChecked />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <AIIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="AI Insights" secondary="Get AI-generated insights and recommendations" />
                                    <Switch defaultChecked />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <TrophyIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Deal Updates" secondary="Notifications when deals change status" />
                                    <Switch defaultChecked />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <TaskIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Task Reminders" secondary="Reminders for tasks and deadlines" />
                                    <Switch defaultChecked />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </TabPanel>
            </Paper>
        </Container>
    );
}